// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import DidItEmptyProfileTab from 'app/common/react/components/do/aggregated-pins/did-it/DidItProfileFeed/DidItEmptyProfileTab';
import DidItFeedItem from 'app/common/react/components/do/aggregated-pins/did-it/common/DidItFeedItem';
import i18n from 'app/common/lib/i18n';
import templateConst from 'app/common/lib/templateConst';
import throttle from 'lodash/throttle';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import Toast from 'app/common/react/ui/Toast';
import type { CommentUser } from 'app/packages/pinterest-flow-types/user';
import type { State as ReduxState } from 'app/common/redux/types';
import withShowToast from 'app/common/lib/react/withShowToast';
import { compose } from 'redux';
import { track } from 'app/common/lib/react/analytics';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import {
  getElementHeight,
  getWindowScrollPos,
  getScrollHeight,
} from 'app/common/lib/react/scrollUtils';
import { Box, Container, Spinner } from 'gestalt';
import { type FeedItemKey, type FeedItemUserDidItDataItem } from 'app/common/redux/reducers/feeds';

const SCROLL_BUFFER = 200;
const SCROLL_THROTTLE_TIMEOUT = 100;
const TOAST_DURATION = 3000;

type OwnProps = {|
  isOwnProfile: boolean,
  triedItUser: CommentUser,
|};

type ReduxProps = {|
  feedItems: Array<FeedItemUserDidItDataItem>,
|};

type HOCProps = {|
  didItUserFeedResource: Resource<FeedItemUserDidItDataItem>,
  showToast: Function,
|};

type ConnectedProps = {| ...OwnProps, ...ReduxProps |};

type Props = {| ...OwnProps, ...ReduxProps, ...HOCProps |};

export class DidItProfileFeed extends Component<Props> {
  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
    track(PCONST.thrift.EventType.VIEW, {
      view: PCONST.thrift.ViewType.USER,
      viewParameter: PCONST.thrift.ViewParameterType.USER_DID_IT,
    });
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  fetchMoreData() {
    const {
      didItUserFeedResource: { isFetching, isAtEnd, fetchMore },
    } = this.props;
    if (!isFetching) {
      if (isAtEnd) {
        window.removeEventListener('scroll', this.handleScroll);
      } else {
        fetchMore();
      }
    }
  }

  removeReportedActivity = (activityId: string) => {
    this.props.showToast(({ onHide }) => (
      <Toast
        duration={TOAST_DURATION}
        href={templateConst.settings.HELP_PIN_POLICIES_URL}
        onHide={onHide}
        text={i18n._(
          'Thanks! We’ll take this down if it goes against our guidelines.',
          'Statement that a tried item has successfully been reported',
        )}
      />
    ));
  };

  handleScroll = throttle(() => {
    const {
      didItUserFeedResource: { isFetching, isAtEnd },
    } = this.props;
    if (isFetching || isAtEnd) {
      return;
    }

    const containerHeight = getElementHeight(window);
    const scrollHeight = getScrollHeight(window);
    const scrollPos = getWindowScrollPos();

    if (scrollHeight - scrollPos - SCROLL_BUFFER < containerHeight) {
      this.fetchMoreData();
    }
  }, SCROLL_THROTTLE_TIMEOUT);

  render() {
    const {
      feedItems,
      isOwnProfile,
      didItUserFeedResource: { isLoaded },
      triedItUser,
    } = this.props;

    if (!isLoaded) {
      return (
        <Container>
          <Box deprecatedMargin={6}>
            <div className="loadingSpinner">
              <Spinner accessibilityLabel={i18n._('Tried it feed')} show />
            </div>
          </Box>
        </Container>
      );
    }

    const timeSpentViewParam = isOwnProfile
      ? PCONST.thrift.ViewParameterType.USER_SELF
      : PCONST.thrift.ViewParameterType.USER_OTHERS;

    if (!feedItems.length && isOwnProfile) {
      return <DidItEmptyProfileTab loggedInUser={triedItUser} />;
    }

    return (
      <Container>
        <TimeSpent
          view={PCONST.thrift.ViewType.USER}
          viewParameter={timeSpentViewParam}
          component={PCONST.thrift.ComponentType.USER_TRIED}
        >
          <Box xs={{ display: 'flex', column: 12 }} justifyContent="center">
            <Box xs={{ column: 12 }} md={{ column: 10 }}>
              {feedItems.map((item) => (
                <Box key={item.id}>
                  <DidItFeedItem
                    didItDataId={item.id}
                    removeReportedActivity={this.removeReportedActivity}
                    viewType={PCONST.thrift.ViewType.USER}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </TimeSpent>
      </Container>
    );
  }
}

const mapStateToProps = (state: ReduxState, props: OwnProps): ReduxProps => {
  const {
    feeds: { userDidItData = {} },
  } = state;
  const userDidItItems: ?Array<FeedItemKey> = userDidItData[props.triedItUser.username];
  return {
    // This is to pacify Flow, which doesn't realize feeds.userDidItData can only contain 'userdiditdata' type items.
    feedItems: (userDidItItems || []).reduce(
      (arr: Array<FeedItemUserDidItDataItem>, item: FeedItemKey) =>
        item.type === 'userdiditdata' ? arr.concat({ type: item.type, id: item.id }) : arr,
      [],
    ),
  };
};

const mapDispatchToProps = () => {};

export default compose(
  connect<ConnectedProps, OwnProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withResource(
    {
      name: 'DidItUserFeedResource',
      key: 'didItUserFeedResource',
      options: (props) => ({
        username: props.triedItUser.username,
        redux_normalize_feed: true,
      }),
    },
    { allowStale: false },
  ),
  withShowToast,
)(DidItProfileFeed);
